import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import ActionBar from '@/components/feature/ActionBar';
import MonthNav from '@/components/feature/MonthNav';
import { useStore } from '@/hooks/useStore';
import type { Retirada } from '@/mocks/store';

function fmt(v: number) { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }

export default function RetiradasPage() {
  const { retiradas, vendas, despesas, produtos, addRetirada, updateRetirada, deleteRetirada, totalRetiradas, retiradaSegura, lucroOperacional } = useStore();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Retirada | null>(null);
  const [form, setForm] = useState({ valor: '', data: new Date().toISOString().split('T')[0] });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());

  const mesPad = String(mes).padStart(2, '0');
  const anoStr = String(ano);

  const retiiradasFiltradas = retiradas.filter(r => {
    if (!r.data) return true;
    const [av, mv] = r.data.split('-');
    return av === anoStr && mv === mesPad;
  });
  const totalFiltrado = retiiradasFiltradas.reduce((acc, r) => acc + r.valor, 0);

  // Cálculo mensal de lucro e retirada segura
  const vendasMes = vendas.filter(v => v.data?.startsWith(`${anoStr}-${mesPad}`));
  const despesasMes = despesas.filter(d => d.data?.startsWith(`${anoStr}-${mesPad}`));
  const totalBrutoMes = vendasMes.reduce((acc, v) => acc + v.valorTotal, 0);
  const custoVendasMes = vendasMes.reduce((acc, v) => {
    const produto = produtos.find(p => p.id === v.produtoId);
    const custoUnitario = produto ? produto.custo + produto.custoExtra : 0;
    return acc + (custoUnitario * v.quantidade);
  }, 0);
  const totalDespesasMes = despesasMes.reduce((acc, d) => acc + d.valor, 0);
  const lucroMes = totalBrutoMes - custoVendasMes - totalDespesasMes;
  const retiradaSeguraMes = Math.max(0, lucroMes - totalFiltrado);

  function openNew() { setEditingItem(null); setForm({ valor: '', data: new Date().toISOString().split('T')[0] }); setShowModal(true); }
  function openEdit(r: Retirada) { setEditingItem(r); setForm({ valor: String(r.valor), data: r.data }); setShowModal(true); }

  function handleSave() {
    if (!form.valor) return;
    if (editingItem) {
      updateRetirada(editingItem.id, { valor: Number(form.valor), data: form.data });
    } else {
      addRetirada({ valor: Number(form.valor), data: form.data });
    }
    setShowModal(false);
    setEditingItem(null);
    setForm({ valor: '', data: new Date().toISOString().split('T')[0] });
  }

  return (
    <AppLayout>
      <TopBar title="Retiradas" subtitle="Saques realizados pelo dono" />
      <ActionBar>
        <MonthNav mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a); }} />
        <button onClick={openNew} className="btn-gold w-full flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap">
          <i className="ri-add-line text-base"></i> Nova Retirada
        </button>
      </ActionBar>
      <div className="pt-[200px] md:pt-[200px] p-3 md:p-6 space-y-3 animate-fade-in-up">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Retirado</p>
            <p className="text-base md:text-xl font-bold font-mono text-orange-400">{fmt(totalFiltrado)}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Lucro Líquido</p>
            <p className={`text-base md:text-xl font-bold font-mono ${lucroMes >= 0 ? 'text-neon' : 'text-red-400'}`}>{fmt(lucroMes)}</p>
          </div>
          <div className={`rounded-xl p-3 border flex flex-col items-center text-center ${retiradaSeguraMes > 0 ? 'bg-neon/5 border-neon/30' : 'bg-dark-card border-dark-border'}`}>
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Disponível</p>
            <p className={`text-base md:text-xl font-bold font-mono ${retiradaSeguraMes > 0 ? 'text-neon' : 'text-dark-muted'}`}>{fmt(retiradaSeguraMes)}</p>
          </div>
        </div>

        {retiradaSegura <= 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center shrink-0"><i className="ri-alert-line text-orange-400 text-sm"></i></div>
              <p className="text-xs text-dark-muted">Lucro atual não cobre novas retiradas. Aguarde mais vendas.</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {retiiradasFiltradas.length === 0 ? (
            <div className="bg-dark-card border border-dark-border rounded-xl p-8 flex flex-col items-center text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card2 mb-2"><i className="ri-arrow-up-circle-line text-xl text-dark-muted"></i></div>
              <p className="text-sm text-dark-muted">Nenhuma retirada neste mês</p>
            </div>
          ) : retiiradasFiltradas.map(r => (
            <div key={r.id} className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-dark-text text-sm">Retirada</p>
                  <p className="text-[10px] text-dark-muted mt-0.5">{new Date(r.data).toLocaleDateString('pt-BR')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-base font-bold font-mono text-orange-400">{fmt(r.valor)}</p>
                  <button onClick={() => openEdit(r)} className="w-7 h-7 flex items-center justify-center rounded-lg text-dark-muted hover:text-neon cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                  <button onClick={() => setConfirmDelete(r.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-dark-muted hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up shadow-[0_0_40px_rgba(201,168,76,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">{editingItem ? 'Editar Retirada' : 'Nova Retirada'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"><i className="ri-close-line text-lg"></i></button>
            </div>
            {retiradaSegura > 0 && !editingItem && (
              <div className="bg-neon/5 border border-neon/20 rounded-lg p-3 mb-3">
                <p className="text-xs text-dark-muted">Disponível para retirada segura: <span className="text-neon font-bold font-mono">{fmt(retiradaSegura)}</span></p>
              </div>
            )}
            <div className="space-y-3">
              <div><label className="text-xs text-dark-muted mb-1 block font-medium">Valor (R$)</label><input type="number" className="input-dark" placeholder="0,00" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} /></div>
              <div><label className="text-xs text-dark-muted mb-1 block font-medium">Data</label><input type="date" className="input-dark" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} /></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">{editingItem ? 'Salvar' : 'Registrar Retirada'}</button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
            <h3 className="text-center text-base font-bold mb-2 text-dark-text">Excluir retirada?</h3>
            <p className="text-center text-sm text-dark-muted mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => { deleteRetirada(confirmDelete); setConfirmDelete(null); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
